"use client";
import { useState } from "react";
import { useMediaQuery } from "@mui/material";
import Image from "next/image";
import { useSession } from "next-auth/react";
import ModalFilters from "./modals/ModalFilters";
import { exploreMap } from "@/api/traveler";
import { useTranslation } from "@/app/i18n/client";
import filterIcon from "@/assets/images/icons/filter-icon.svg";
import mapIcon from "@/assets/images/icons/map.svg";
import CustomButton from "@/common/customElements/button";
import { CircularDiv, CustomDiv, InlineDiv } from "@/common/divs";
import { AlmostBoldText, SemiBoldText } from "@/common/texts";
import {
  CategoryType,
  HomeShowFiltersProps,
  LanguageType,
  TagType,
} from "@/types/components";
import {
  NEUTRO_TITLES_COLOR,
  PRIMARY_BUTTON_COLOR,
  SECONDARY_BUTTON_COLOR,
} from "@/utils/constants";

const INITIAL_DISTANCE = 50000;

const HomeFilters: React.FC<HomeShowFiltersProps> = ({
  city,
  valuesState,
  categories,
  parseTerm,
  changeValuesForFetch,
  tags,
  mobilityTags,
  setIsMapView,
  languages,
}) => {
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const { data: session } = useSession();
  const getParsedTerm = (term: string) => {
    if (term.includes("tag:")) {
      const tag = tags.current.find(
        (tag: TagType) => tag.id === parseInt(term.replace("tag:", "")),
      );
      if (tag) {
        return t(tag.i18_name);
      } else {
        return term;
      }
    } else if (term.includes("mob:")) {
      const mob = mobilityTags.current.find(
        (mob: TagType) => mob.id === parseInt(term.replace("mob:", "")),
      );
      if (mob) {
        return t(mob.i18_name);
      } else {
        return term;
      }
    } else if (term.includes("cat:")) {
      const category = categories.current.find(
        (category: CategoryType) =>
          category.id === parseInt(term.replace("cat:", "")),
      );
      if (category) {
        return t(category.i18_name);
      } else {
        return term;
      }
    } else if (term.includes("subcat:")) {
      return term.replace("subcat:", "");
    } else {
      return term;
    }
  };
  const parseDate = (date: string) => {
    const dateArray = date.split("-");
    return `${dateArray[2]}/${dateArray[1]}/${dateArray[0]}`;
  };
  const isMobile = useMediaQuery("(max-width:450px)");

  const getStringTerm = (term: string): string => {
    const arrayOfTerms = term.split(" ");
    //we have to remove all the terms starting by cat: or subcat: or tag: or mob:
    return arrayOfTerms
      .filter(
        (term: string) =>
          !(
            term.includes("cat:") ||
            term.includes("mob") ||
            term.includes("subcat") ||
            term.includes("tag")
          ),
      )
      .join(" ");
  };

  const stringTerm = getStringTerm(valuesState.current.term ?? "");

  const { t } = useTranslation();
  return (
    <>
      <CustomDiv className="filters-div" width="100%" paddingX="108px">
        <section style={{ paddingTop: "20px", width: "60%" }}>
          <div style={{ marginBottom: "10px" }}>
            <AlmostBoldText color={NEUTRO_TITLES_COLOR}>
              {t("activities.filter.applied_filters")}:
            </AlmostBoldText>
          </div>
          <InlineDiv style={{ gap: "8px", flexWrap: "wrap" }}>
            <CircularDiv
              background={PRIMARY_BUTTON_COLOR}
              height="32px"
              paddingX="16px"
              width="fit-content"
            >
              <SemiBoldText
                fontSize="14px"
                color={NEUTRO_TITLES_COLOR}
                style={{ marginTop: "-4px" }}
              >
                {valuesState.current.city ? valuesState.current.city : city}
              </SemiBoldText>
            </CircularDiv>
            {valuesState.current.term &&
              valuesState.current.term
                .split(" ")
                .map((term: string, index: number) =>
                  (
                    term !== "" &&
                    (term.includes("cat:") ||
                      term.includes("mob") ||
                      term.includes("subcat") ||
                      term.includes("tag"))
                  ) ?
                    <CircularDiv
                      key={index}
                      background={PRIMARY_BUTTON_COLOR}
                      height="32px"
                      paddingX="16px"
                      width="fit-content"
                    >
                      <SemiBoldText
                        fontSize="14px"
                        color={NEUTRO_TITLES_COLOR}
                        style={{ marginTop: "-4px" }}
                      >
                        {getParsedTerm(term)}
                      </SemiBoldText>
                    </CircularDiv>
                  : null,
                )}
            {stringTerm != "" && (
              <CircularDiv
                background={PRIMARY_BUTTON_COLOR}
                height="32px"
                paddingX="16px"
                width="fit-content"
              >
                <SemiBoldText
                  fontSize="14px"
                  color={NEUTRO_TITLES_COLOR}
                  style={{ marginTop: "-4px" }}
                >
                  {stringTerm}
                </SemiBoldText>
              </CircularDiv>
            )}
            {valuesState.current.date && (
              <CircularDiv
                background={PRIMARY_BUTTON_COLOR}
                height="32px"
                paddingX="16px"
                width="fit-content"
              >
                <SemiBoldText
                  fontSize="14px"
                  color={NEUTRO_TITLES_COLOR}
                  style={{ marginTop: "-4px" }}
                >
                  {parseDate(valuesState.current.date)}
                </SemiBoldText>
              </CircularDiv>
            )}
            {(valuesState.current.minPrice || valuesState.current.maxPrice) && (
              <CircularDiv
                background={PRIMARY_BUTTON_COLOR}
                height="32px"
                paddingX="16px"
                width="fit-content"
              >
                <SemiBoldText
                  fontSize="14px"
                  color={NEUTRO_TITLES_COLOR}
                  style={{ marginTop: "-4px" }}
                >
                  {t("activity.from")} {valuesState.current.minPrice} €{" "}
                  {t("activity.to")} {valuesState.current.maxPrice} €
                </SemiBoldText>
              </CircularDiv>
            )}
            {valuesState.current.hours && (
              <CircularDiv
                background={PRIMARY_BUTTON_COLOR}
                height="32px"
                paddingX="16px"
                width="fit-content"
              >
                <SemiBoldText
                  fontSize="14px"
                  color={NEUTRO_TITLES_COLOR}
                  style={{ marginTop: "-4px" }}
                >
                  {t("activity.map.duration")} :{" "}
                  {valuesState.current.hours == 5 ?
                    "+" + valuesState.current.hours
                  : valuesState.current.hours}
                  h
                </SemiBoldText>
              </CircularDiv>
            )}
            {valuesState.current.distance ?
              valuesState.current.distance != INITIAL_DISTANCE && (
                <CircularDiv
                  background={PRIMARY_BUTTON_COLOR}
                  height="32px"
                  paddingX="16px"
                  width="fit-content"
                >
                  <SemiBoldText
                    fontSize="14px"
                    color={NEUTRO_TITLES_COLOR}
                    style={{ marginTop: "-4px" }}
                  >
                    {t("activities.filter.from_distance")}{" "}
                    {valuesState.current.distance / 1000} km
                  </SemiBoldText>
                </CircularDiv>
              )
            : null}
            {valuesState.current?.languages
              ?.split(",")
              .map((language: string, index: number) => (
                <CircularDiv
                  key={index}
                  background={PRIMARY_BUTTON_COLOR}
                  height="32px"
                  paddingX="16px"
                  width="fit-content"
                >
                  <SemiBoldText
                    fontSize="14px"
                    color={NEUTRO_TITLES_COLOR}
                    style={{ marginTop: "-4px" }}
                  >
                    {
                      languages.current.find(
                        (lang: LanguageType) => lang.id === parseInt(language),
                      ).name
                    }
                  </SemiBoldText>
                </CircularDiv>
              ))}
          </InlineDiv>
        </section>
        <CustomDiv style={{ paddingTop: "40px" }}>
          <InlineDiv className="wrap-mobile-div" style={{ gap: "24px" }}>
            <CustomButton
              width={isMobile ? "192px" : "257px"}
              height="43px"
              onClick={() => {
                setOpenFilters(true);
              }}
              backgroundColor={SECONDARY_BUTTON_COLOR}
              borderColor={NEUTRO_TITLES_COLOR}
            >
              <InlineDiv className="centerDiv">
                <AlmostBoldText
                  style={{ marginTop: "-5px" }}
                  color={NEUTRO_TITLES_COLOR}
                >
                  {t("activities.filter.advanced_filters")}
                </AlmostBoldText>
                <Image
                  style={{
                    marginTop: "1px",
                    marginLeft: "12px",
                    width: "22px",
                    height: "15px",
                  }}
                  src={filterIcon.src}
                  alt="filter"
                  width={22}
                  height={15}
                />
              </InlineDiv>
            </CustomButton>
            <CustomButton
              width={isMobile ? "160px" : "192px"}
              height="43px"
              onClick={() => {
                if (session) {
                  exploreMap();
                }
                setIsMapView(true);
              }}
              backgroundColor={SECONDARY_BUTTON_COLOR}
              borderColor={NEUTRO_TITLES_COLOR}
            >
              <InlineDiv className="centerDiv">
                <AlmostBoldText
                  style={{ marginTop: "-4px" }}
                  color={NEUTRO_TITLES_COLOR}
                >
                  {t("activities.filter.map_view")}
                </AlmostBoldText>
                <Image
                  style={{ marginTop: "0px", marginLeft: "12px" }}
                  src={mapIcon.src}
                  alt="map"
                  width={20}
                  height={20}
                />
              </InlineDiv>
            </CustomButton>
          </InlineDiv>
        </CustomDiv>
      </CustomDiv>
      {openFilters && (
        <ModalFilters
          open={openFilters}
          setOpen={setOpenFilters}
          valuesState={valuesState}
          categories={categories}
          tags={tags}
          parseTerm={parseTerm}
          changeValuesForFetch={changeValuesForFetch}
          mobilityTags={mobilityTags}
          languages={languages}
        />
      )}
    </>
  );
};

export default HomeFilters;
